import {Row, Col, Button, Table, Alert, message, Drawer, Space} from "antd";
import {StyledCustomersDataBox} from "../../../styles/customers/customers";
import {
  useGetSalesInsights,
  useGetAttendees,
  useExportAttendeesCSV,
  AttendeeAnalytics,
} from "../../../features/live-event-analytics";
import {useParams} from "react-router";
import {CloseOutlined, DownloadOutlined} from "@ant-design/icons";
import type {ColumnsType} from "antd/es/table";
import {
  StyledUpcomingCallsWrapper,
  StyledNoUpcomingCallBox,
} from "../../calls/upcoming-calls/upcoming-calls.style";
import {StyledDescriptionText, StyledPageHeader} from "../../messages/messages.style";
import {FullPageLoader} from "../../full-page-loader";
import {CustomerMetricsSkeleton} from "../../customers/customer-details-section/customer-metrics-skeleton";
import {AttendeesDetailsModal} from "./details/details-modal";
import {Fragment, useState} from "react";
import {StyledRevenueListItem} from "../../products/sales-and-insights/revenue-section/revenue-section.style";
import {CustomPagination} from "../../pagination";
interface DataType {
  key: string;
  name: string;
  email: string;
  reference: string;
}

function AttendeesComponent() {
  const [page, setPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAttendee, setSelectedAttendee] = useState<AttendeeAnalytics>();
  const {id} = useParams();

  const {
    data: attendeesData,
    isLoading: attendeesLoading,
    isError: attendeesError,
    error: attendeesErrorData,
    isFetching,
    isPreviousData,
  } = useGetAttendees(id, page);

  const exportCsvQueryMutation = useExportAttendeesCSV();

  const {
    data: salesInsight,
    isLoading: salesLoading,
    isError: salesError,
    error: salesErrorData,
  } = useGetSalesInsights(id);

  const [openRevenueBreakDown, setOpenRevenueBreakDown] = useState(false);

  const showRevenueBreakDown = () => {
    setOpenRevenueBreakDown(true);
  };

  const onCloseRevenueBreakDown = () => {
    setOpenRevenueBreakDown(false);
  };

  const handleOk = () => {
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  if (attendeesLoading || salesLoading) {
    return <FullPageLoader />;
  }
  if (attendeesError) {
    return (
      <Alert
        message="Error"
        description={attendeesErrorData?.message}
        type="error"
        showIcon
      />
    );
  }
  if (salesError) {
    return (
      <Alert
        message="Error"
        description={salesErrorData?.message}
        type="error"
        showIcon
      />
    );
  }

  const showModal = (reference: string) => {
    const attendee = attendeesData?.attendees.find(
      (member) => member.reference === reference
    );
    setSelectedAttendee(attendee);
    setIsModalOpen(true);
  };

  const tableData: DataType[] = (attendeesData?.attendees ?? []).map((attendee) => ({
    key: attendee.key,
    name: attendee.fullName,
    email: attendee.emailAddress,
    reference: attendee.reference,
  }));

  const columns: ColumnsType<DataType> = [
    {
      title: "Name",
      dataIndex: "name",
      fixed: "left",
      width: "17.425rem",
      key: "name",
      render: (text) => (
        <>
          <StyledDescriptionText style={{textTransform: "capitalize"}}>
            {text}
          </StyledDescriptionText>
        </>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      width: "17.425rem",
      key: "email",
      render: (text) => (
        <>
          <StyledDescriptionText>{text}</StyledDescriptionText>
        </>
      ),
    },
    {
      title: "Reference",
      dataIndex: "reference",
      width: "17.425rem",
      key: "reference",
      render: (text) => (
        <>
          <StyledDescriptionText>{text}</StyledDescriptionText>
        </>
      ),
    },
    {
      title: "",
      key: "action",
      render: (_, record) => (
        <Button size="small" type="link" onClick={() => showModal(record.key)}>
          <span style={{textDecoration: "underline"}}>View details</span>
        </Button>
      ),
    },
  ];

  const handleExportAsCSV = () => {
    if (id) {
      exportCsvQueryMutation.mutate(
        {eventId: id},
        {
          onSuccess: (data) => {
            message.success("File ready to be downloaded!");
            const url = window.URL.createObjectURL(new Blob([data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "Live-class-attendee.csv");
            document.body.appendChild(link);
            link.click();
          },
        }
      );
    }
  };
  return (
    <div>
      {isFetching ? (
        <CustomerMetricsSkeleton />
      ) : (
        <div>
          <Space size={16} direction="vertical" style={{display: "flex"}}>
            <Row gutter={[32, 20]}>
              <Col span={24} md={12}>
                <StyledCustomersDataBox
                  style={{
                    backgroundColor: "#EAE6F3",
                    color: "#272037",
                    borderRadius: "5px",
                    border: "1.6px solid #43375D",
                  }}
                >
                  <div style={{backgroundColor: "#B3A8CD"}} className="data-circle"></div>
                  <p className="data-box-title">Total revenue</p>
                  <h2 className="data-box-value">
                    {salesInsight?.getPrimaryRevenue()?.currency ?? "NGN"}{" "}
                    {salesInsight?.getPrimaryRevenue()?.amount ?? 0}
                  </h2>
                </StyledCustomersDataBox>
              </Col>
              <Col span={24} md={12}>
                <StyledCustomersDataBox
                  style={{
                    backgroundColor: "#E7EDE1",
                    color: "#16350F",
                    borderRadius: "5px",
                    border: "1.6px solid #355D2C",
                  }}
                >
                  <div style={{backgroundColor: "#B6C2A8"}} className="data-circle"></div>
                  <p className="data-box-title">Total attendees</p>
                  <h2 className="data-box-value">{salesInsight?.totalAttendees ?? 0}</h2>
                </StyledCustomersDataBox>
              </Col>
            </Row>
            {salesInsight?.hasMultipleCurrencies() && (
              <p style={{color: "#595959"}}>
                Check your earnings in all your currencies,{" "}
                <Button
                  onClick={showRevenueBreakDown}
                  style={{padding: "0"}}
                  type="link"
                  size="small"
                >
                  click here
                </Button>{" "}
                to view
              </p>
            )}
          </Space>
        </div>
      )}

      <div style={{marginTop: "7rem"}}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "1rem",
            marginBottom: "4rem",
          }}
        >
          <StyledPageHeader>Attendees</StyledPageHeader>
          <div>
            <Button
              onClick={handleExportAsCSV}
              disabled={tableData.length < 1}
              block
              icon={<DownloadOutlined style={{fontSize: "1.6rem"}} />}
              type="text"
              loading={exportCsvQueryMutation.isLoading}
            >
              <span style={{textDecoration: "underline"}}>Export CSV</span>
            </Button>
          </div>
        </div>
        {tableData.length < 1 ? (
          <StyledUpcomingCallsWrapper style={{paddingTop: 0}}>
            <StyledNoUpcomingCallBox>
              <p className="info-text">Your attendees will appear here</p>
            </StyledNoUpcomingCallBox>
          </StyledUpcomingCallsWrapper>
        ) : (
          <>
            <Table
              columns={columns}
              dataSource={tableData}
              loading={isFetching}
              scroll={{x: 100}}
              pagination={false}
            />
            <CustomPagination
              isPreviousData={isPreviousData}
              page={page}
              paginationMeta={attendeesData?.legacyMeta}
              setPage={setPage}
            />
          </>
        )}
      </div>
      <AttendeesDetailsModal
        visibleState={isModalOpen}
        attendeeId={selectedAttendee?.legacyId}
        eventId={id}
        attendeeEmail={selectedAttendee?.emailAddress}
        inlineAttendee={selectedAttendee}
        cancelFunc={handleCancel}
        okFunc={handleOk}
      />
      <Drawer
        title="Revenue breakdown"
        placement="right"
        onClose={onCloseRevenueBreakDown}
        visible={openRevenueBreakDown}
        // mask={false}
        closable={false}
        width={320}
        extra={
          <Button
            type="ghost"
            size="small"
            icon={<CloseOutlined />}
            onClick={onCloseRevenueBreakDown}
          ></Button>
        }
      >
        <Row gutter={[20, 16]}>
          {salesInsight?.getRevenueBreakdown().map((revenue) => (
            <Fragment key={revenue.currency}>
              <Col span={12}>
                <StyledRevenueListItem>
                  <div className="item-volume">
                    <p className="item-title">Currency</p>
                    <p className="item-value">{revenue.currency}</p>
                  </div>
                </StyledRevenueListItem>
              </Col>
              <Col span={12}>
                <StyledRevenueListItem>
                  <div className="item-unit">
                    <p className="item-title">Total amount</p>
                    <p className="item-value">
                      {Number(revenue.amount).toLocaleString("en-us")}
                    </p>
                  </div>
                </StyledRevenueListItem>
              </Col>
            </Fragment>
          ))}
        </Row>
      </Drawer>
    </div>
  );
}

export {AttendeesComponent};
